import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import AppSwitcher from '../../components/AppSwitcher';
import TwoFactorStep from '../../components/TwoFactorStep';
import { buildApiUrl } from './supportApi';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess } from '../../slices/authSlice';

// The help center backend identifies people by the `id` claim in the admin token —
// the one /api/admin/auth/login mints. A Ginox *user* token is rejected with a 401 before any
// lookup: it carries `username` and no `id`, and since both are signed with the same secret,
// that payload difference is the only thing separating an admin from a customer.
//
// That token is now the one shared admin session, so signing in here signs you in to every
// panel. This page still exists for anyone landing on the support URL directly; arriving via
// the header's app switcher skips it entirely.
//
// Being listed in the backend's Admin table (keyed on that same `id`) is what grants access —
// the token's `role` claim is not read, so promotion cannot be granted from this side.
const AUTH_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/admin/auth`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sharedToken = useSelector((s) => s.auth.access_token);

  // 2FA step: login now returns a tempToken instead of an access_token.
  const [twoFA, setTwoFA] = useState(null); // { method, tempToken }
  const [code, setCode] = useState('');

  // Already signed in somewhere else? Go straight through — SupportLayout runs the
  // /admin/me gate, so a non-admin still gets told so rather than sneaking in.
  useEffect(() => {
    if (sharedToken) {
      navigate('/support-system/dashboard');
    }
  }, [navigate, sharedToken]);

  // Confirm the signed-in person is actually a support admin, then enter the dashboard.
  //
  // The Ginox login hands a token to every user, so it proves identity and nothing more.
  // /admin/me is the only thing that can answer "is this person an admin", and asking it
  // here means a non-admin never gets a stored session or a half-working panel — just a
  // clear message on the login screen.
  const finishLogin = async (data) => {
    try {
      // Asked with the raw token rather than supportApiFetch: the session is not stored
      // yet, and a non-admin should never get one.
      const res = await fetch(buildApiUrl('api/v1/admin/me'), {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });

      if (res.status !== 200) {
        setError(
          res.status === 403
            ? 'This account does not have support admin access.'
            : 'Could not verify admin access. Please try again.'
        );
        return;
      }

      const me = await res.json();
      // role is not part of /admin/me — the panel's own pages read it, so stamp it here.
      localStorage.setItem('supportUser', JSON.stringify({ ...me, role: 'admin' }));
      dispatch(loginSuccess(data));
      navigate('/support-system/dashboard');
    } catch (err) {
      setError('Network error. Please check your connection.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${AUTH_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      // 2FA is the normal path; the direct branch below is the fallback for an account
      // that has none. `method` names the step, so it is passed through as-is.
      if (data.twoFARequired) {
        setCode('');
        setTwoFA({ method: data.method, tempToken: data.tempToken });
      } else if (response.ok && data.access_token) {
        await finishLogin(data);
      } else {
        setError(data.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (value = code) => {
    if (value.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const endpoint = twoFA.method === 'email' ? 'login/email-2fa-verify' : 'login/2fa-verify';
      const response = await fetch(`${AUTH_BASE}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: value, tempToken: twoFA.tempToken }),
      });
      const data = await response.json();
      if (response.ok && data.access_token) {
        await finishLogin(data);
      } else {
        setError(data.message || 'Invalid or expired code');
        setCode('');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const backToLogin = () => {
    setTwoFA(null);
    setCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="absolute top-6 right-6 z-50">
        <AppSwitcher />
      </div>

      <div className="w-full max-w-sm">
        {/* Logo + title */}
        <div className="mb-8 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl border border-ginox-green/30 bg-ginox-green/10 mb-5">
            <img
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt="Ginox"
              className="h-8 w-8 object-contain"
            />
          </div>
          <div className="flex items-center justify-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-5 bg-ginox-green" aria-hidden="true" />
            Support
            <span className="h-px w-5 bg-ginox-green" aria-hidden="true" />
          </div>
          <h1 className="font-serif italic text-[32px] leading-[1.05] text-foreground">
            Support <span className="text-gradient-ginox">Admin</span>
          </h1>
          <p className="mt-1 text-xs text-muted-foreground font-mono tracking-[0.06em]">
            {twoFA ? 'Two-factor verification' : 'Sign in to your account'}
          </p>
        </div>

        {/* Form card */}
        <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl">
          {twoFA ? (
            <TwoFactorStep
              method={twoFA.method}
              email={email}
              code={code}
              onCodeChange={setCode}
              onComplete={handleVerify}
              onVerify={() => handleVerify()}
              onBack={backToLogin}
              loading={isLoading}
              error={error}
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              />
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-md bg-ginox-green px-4 py-2.5 text-sm font-semibold text-black hover:bg-ginox-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
